/// <reference path="../../../../../typings/jquery.d.ts"/>
/// <reference path="../../../../../../shared/models/generated/ConsentManagementConsMgmntStatusResponseApi.d.ts"/>

import { AppFormModel } from '../../models/AppFormModel';
import { ApplicationPages } from '../../Constants';
import { ConfirmationPageController } from "./ConfirmationPageController";
import { AppApiService } from '../../services/AppApiService';
import { AppViewService } from '../../services/AppViewService';
import { AppPersistenceService } from '../../services/AppPersistenceService';
import { User } from '../../../../../../shared/models/api/User/User';
import { ViewConfiguration } from '../../../../../../shared/services/BasicViewService';
import { BasicValidationService } from '../../../../../../shared/services/BasicValidationService';
import { BasicApplicationService } from '../../../../../../shared/services/BasicApplicationService';
import { AdvertisingConsentModel } from '../../../../../../shared/models/view/AdvertisingConsent';
import { AppUtils } from '../../../../../../shared/utils/index';

export class InputPageController implements StepController, StandardViewEvents {
	private readonly $ctx: JQuery;
	private readonly user: User;
	private readonly options: BasicAppOptions<AdvertisngConsentAppDefinition>;
	private readonly appApiService: AppApiService;
	private readonly appViewService: AppViewService;
	private readonly appPersistenceService: AppPersistenceService;
	private readonly appApplicationService: BasicApplicationService<AdvertisngConsentAppDefinition>;
	private readonly parent: AppController;
	private readonly loader: BasicLoader<AdvertisngConsentAppDefinition>;
	private appValidationService?: BasicValidationService<AdvertisngConsentAppDefinition>;
	private anzeigen = true;
	private anzeigeEmail = true;
	private anzeigeTelefon1 = true;
	private anzeigeTelefon2 = true;
	private anzeigeTracking = true;

	constructor(parent: AppController, options: BasicAppOptions<AdvertisngConsentAppDefinition>, loader: BasicLoader<AdvertisngConsentAppDefinition>, user: User) {
		this.parent = parent;
		this.options = options;
		this.loader = loader;
		this.user = user;
		this.$ctx = $(options.$ctx);

		this.appPersistenceService = new AppPersistenceService(options);
		this.appViewService = new AppViewService(parent, options, loader);
		this.appApiService = new AppApiService(options);
		this.appApplicationService = new BasicApplicationService(options);
	}

	public start(): void {
		this.requestIpAndStartApplication();
	}

	public requestIpAndStartApplication(): void {
		this.appApiService.getUserIp(this.startApplication.bind(this), this.handleApplicationError.bind(this));
	}

	public startApplication(data: { IP: string }): void {
		this.handleGetUserIpSuccess(data);
		if (this.user && this.user.Valid) {
			this.checkAgeAndConsentStatusAndStartRendering();
		} else {
			this.startRendering();
		}
	}

	public checkAgeAndConsentStatusAndStartRendering(): void {
		const shouldCheckStatus = this.isAtLeastAge(this.user.UserData.Geburtsdatum, 16);
		if (shouldCheckStatus) {
			const formModel = this.getMergedFormModel();
			this.appPersistenceService.setFormModelToStore(formModel);
			this.appApiService.checkConsentmanagementStatus(this.handleCheckConsentmanagementStatusSuccess.bind(this), this.handleApplicationError.bind(this));
		} else {
			this.startRendering();
		}
	}

	public startRendering(): void {
		const viewConfig: ViewConfiguration<AdvertisngConsentAppDefinition> = {
			data: this.getViewOptions(),
			templateurl: this.options.app.currenttemplate.templateurl,
			campaignurls: this.options.app.campaignurls,
			parent: this,
			$ctx: this.$ctx,
			options: this.options,
			callback: this.afterRenderApp
		};
		this.appViewService.render(viewConfig);
	}

	/**
	 * returns view options for the page.
	 * only variables defined in here are available in the view to use with handlebars.
	 */
	private getViewOptions(): { [key: string]: any } {
		const formData = this.getFormModelForUser();

		const hasPhoneData = !AppUtils.Helper.isEmptyString(formData.PersonalData.TelefonNummer);

		const hasWorkPhoneData = !AppUtils.Helper.isEmptyString(formData.PersonalData.TelefonGeschaeftlichNummer);

		const hasEmailData =
			!AppUtils.Helper.isEmptyString(formData.PersonalData.Email);

		const hasDataFromToken = hasPhoneData || hasWorkPhoneData || hasEmailData;
		return {
			User: this.user,
			HasDataFromToken: hasDataFromToken,
			Form: formData,
			Anzeigen: this.anzeigen,
			AnzeigeEmail: this.anzeigeEmail,
			AnzeigeTelefon1: this.anzeigeTelefon1,
			AnzeigeTelefon2: this.anzeigeTelefon2,
			AnzeigeTracking: this.anzeigeTracking
		};
	}

	private getMergedFormModel(model?: AppFormModel): AppFormModel {
		const formModel = model ? model : this.appPersistenceService.getFormModelFromStore();
		if (this.user && this.user.Valid) {
			formModel.mergeWithUser(this.user, false, true);
			formModel.PersonalData.Mitgliedsnummer = this.user.MglNo;
		}
		return formModel;
	}

	public afterRenderApp(): void {
		this.appViewService.setConfigAutocompleteAddress();
		this.loader.loadSubApps({ options: this.options }, () => {
			if (this.options.app.usemglcheckwithvesapi) {
				this.options.app.validatorRules['rules']['Mitgliedsnummer'].membernumbercheck = false;
			} else {
				this.options.app.validatorRules['rules']['Mitgliedsnummer'].membernumberwithvescheck = false;
			}
			this.appValidationService = new BasicValidationService({ options: this.options }, this.appViewService.getForm());
			this.parent.afterRenderApp();
			this.appViewService.addListeners(this);
			let advertisingConsent = this.appPersistenceService.getAdvertisingConsent();
			advertisingConsent = this.enrichAdvertisingModel(advertisingConsent);
			this.appPersistenceService.setAdvertisingConsent(advertisingConsent);
			AppUtils.Helper.initRteAuthLinks();
			AppUtils.View.stopLoader();
			AppUtils.View.scrollTop();
		});
	}

	private isAtLeastAge(dob: string, minAge: number): boolean {
		const birthdate = moment(dob, "YYYY-MM-DD");
		const today = moment();
		const age = today.diff(birthdate, 'years');
		const hasBirthdayPassedThisYear = today.isSameOrAfter(birthdate.add(age, 'years'));
		return hasBirthdayPassedThisYear ? age >= minAge : age - 1 >= minAge;
	}

	private enrichAdvertisingModel(advertisingConsentModel: AdvertisingConsentModel): AdvertisingConsentModel {
		advertisingConsentModel.CompanyIdentifier = this.options.app.companyidentifier;
		advertisingConsentModel.CompanyIdentifierReferenceCode = this.options.app.companyidentifierreferencecode;
		advertisingConsentModel.TrackingConsentVersion = this.options.app.trackingconsentversion;
		advertisingConsentModel.TrackingReferenceId = this.options.app.trackingreferenceid;

		return advertisingConsentModel;
	}

	//eslint-disable-next-line
	public viewChangeListener(event: JQuery.Event): void {
		AppUtils.Helper.scheduleTask(() => {
			let formModel = this.appPersistenceService.getFormModelFromStore();
			formModel = this.appViewService.getFormModelFromView(formModel);
			let advertisingConsent = this.appViewService.getAdvertisingConsentModel();
			advertisingConsent = this.enrichAdvertisingModel(advertisingConsent);
			this.appPersistenceService.setAdvertisingConsent(advertisingConsent);
			this.appPersistenceService.setFormModelToStore(formModel);
		}, 'user-visible');
	}

	private getFormModelForUser(formModel?: AppFormModel): AppFormModel {
		const model = formModel ? formModel : this.appPersistenceService.getFormModelFromStore();
		if (this.user && this.user.Valid) {
			model.mergeWithUser(this.user, false, true);
			this.appPersistenceService.setFormModelToStore(model);
		}
		return model;
	}

	public submitListener(event: JQuery.Event): void {
		event.preventDefault();
		if (this.appValidationService && this.appValidationService.isValid()) {
			AppUtils.View.startLoader();
			this.appApiService.getProcessId("AdvertisingConsentApplication",
				this.handleGetProcessIdSuccess.bind(this),
				this.hideFormAndShowErrorText.bind(this));
		}
	}

	private handleGetProcessIdSuccess(data: ApiResponse<string>): void {
		if (data && data.Data) {
			const formModel = this.appPersistenceService.getFormModelFromStore();
			const advertisingConsentModel = this.appPersistenceService.getAdvertisingConsent();

			formModel.ProcessId = parseInt(data.Data);
			this.appPersistenceService.setFormModelToStore(formModel);
			this.appApiService.submitRequest(
				this.user,
				formModel,
				advertisingConsentModel,
				this.handleSubmitSuccess.bind(this),
				this.handleSubmitFailure.bind(this));
		}
	}

	private handleGetUserIpSuccess(data: { IP: string }): void {
		const formModel = this.appPersistenceService.getFormModelFromStore();
		formModel.Ip = data.IP;
		this.appPersistenceService.setFormModelToStore(formModel);
	}

	private handleCheckConsentmanagementStatusSuccess(data: ApiResponse<ConsMgmntStatusResponseApi>): void {
		if (data && data.Data) {
			this.anzeigen = data.Data.Anzeigen;
			this.anzeigeEmail = data.Data.AnzeigeEmail;
			this.anzeigeTelefon1 = data.Data.AnzeigeTelefon1;
			this.anzeigeTelefon2 = data.Data.AnzeigeTelefon2;
			this.anzeigeTracking = data.Data.AnzeigeTracking;
		}
		this.startRendering();
	}

	private handleApplicationError(): void {
		this.anzeigen = false;
		this.startRendering();
	}

	private handleSubmitSuccess(data: ApiResponse<boolean>): void {
		if (data.Success) {
			this.renderConfirmationPage();
		}
		else {
			this.hideFormAndShowErrorText();
		}
	}

	private handleSubmitFailure(jqxhr: JQueryXHR): void {
		AppUtils.View.stopLoader();
		if (jqxhr && jqxhr.responseJSON && jqxhr.responseJSON.Messages) {
			const responseErrors = jqxhr.responseJSON.Messages as any[];
			if (responseErrors.length > 0) {
				responseErrors.forEach((errorObj) => {
					const errorId = errorObj.Id;
					switch (errorId) {
						case 300000:
						case 300004:
						case 300005:
						case 300007:
						case 300009:
							this.renderConfirmationPage();
							break;
						case 300006:
							this.hideFormAndShowErrorText();
							break;
						default:
							this.appViewService.showErrorLayer();
							break;
					}
				});
			}
		}
		else {
			this.appViewService.showErrorLayer();
		}
	}

	private hideFormAndShowErrorText(): void {
		this.appViewService.toggleErrorText(false);
		this.appViewService.toggleMainForm(true);
		AppUtils.View.stopLoader();
	}

	private renderConfirmationPage(): void {
		const confirmationPageOptions = this.appApplicationService.preparePageOptions(ApplicationPages.Confirmation);
		const confirmationPageController = new ConfirmationPageController(this.parent, confirmationPageOptions, this.loader, this.user);
		confirmationPageController.start();
	}
}
